import { useState } from "react";
import { toast } from "sonner";
import { Mail, Clock, CheckCircle2 } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

const services = [
  "Website Design & Build",
  "Google Business Profile",
  "Basic SEO",
  "Social Media Setup",
  "Branding & Logo Design",
  "Not sure — help me choose",
];

const budgets = ["Under $300", "$300 – $600", "$600 – $1,000", "$1,000+"];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    business_name: "",
    service_interest: "",
    budget: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please add your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads", form);
      setDone(true);
      toast.success("Thanks! We'll be in touch within 1 business day.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 sm:py-32">
      <div className="mx-auto max-w-6xl px-6 sm:px-12">
        <div className="grid gap-12 lg:grid-cols-2">
          {/* Left */}
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#75C69F]">
              Get in touch
            </span>
            <h2 className="mt-4 font-heading text-4xl font-medium tracking-tight text-[#0B281B] sm:text-5xl">
              Let's get your business online.
            </h2>
            <p className="mt-5 max-w-md text-lg text-[#4A6657]">
              Tell us about your business and we will get in touch with you within
              one business day.
            </p>

            <div className="mt-10 space-y-5">
              <div className="flex items-center gap-3 text-[#0B281B]">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#F0F5F2]">
                  <Mail className="h-5 w-5 text-[#0B281B]" />
                </span>
                hello@sprigbuilt.com
              </div>
              <div className="flex items-center gap-3 text-[#0B281B]">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#F0F5F2]">
                  <Clock className="h-5 w-5 text-[#0B281B]" />
                </span>
                Replies within 1 business day
              </div>
            </div>
          </div>

          {/* Right - form */}
          <div className="rounded-3xl border border-[#DCECE3] bg-white p-8 shadow-[0_4px_24px_rgba(11,40,27,0.04)]">
            {done ? (
              <div
                className="flex h-full flex-col items-center justify-center py-16 text-center"
                data-testid="contact-success"
              >
                <CheckCircle2 className="h-14 w-14 text-[#75C69F]" />
                <h3 className="mt-5 font-heading text-2xl font-medium text-[#0B281B]">
                  Request received!
                </h3>
                <p className="mt-2 max-w-xs text-[#4A6657]">
                  Thanks for reaching out. We'll review your details and get back to
                  you within one business day.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="Your name *">
                    <input
                      data-testid="contact-name"
                      className={inputCls}
                      value={form.name}
                      onChange={(e) => update("name", e.target.value)}
                      placeholder="Jane Doe"
                    />
                  </Field>
                  <Field label="Email *">
                    <input
                      data-testid="contact-email"
                      type="email"
                      className={inputCls}
                      value={form.email}
                      onChange={(e) => update("email", e.target.value)}
                      placeholder="jane@business.com"
                    />
                  </Field>
                </div>

                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="Phone">
                    <input
                      data-testid="contact-phone"
                      className={inputCls}
                      value={form.phone}
                      onChange={(e) => update("phone", e.target.value)}
                      placeholder="(902) 555-0123"
                    />
                  </Field>
                  <Field label="Business name">
                    <input
                      data-testid="contact-business"
                      className={inputCls}
                      value={form.business_name}
                      onChange={(e) => update("business_name", e.target.value)}
                      placeholder="Your Business"
                    />
                  </Field>
                </div>

                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="Service interest">
                    <Select onValueChange={(v) => update("service_interest", v)}>
                      <SelectTrigger className={inputCls} data-testid="contact-service">
                        <SelectValue placeholder="Choose a service" />
                      </SelectTrigger>
                      <SelectContent>
                        {services.map((s) => (
                          <SelectItem key={s} value={s}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Budget">
                    <Select onValueChange={(v) => update("budget", v)}>
                      <SelectTrigger className={inputCls} data-testid="contact-budget">
                        <SelectValue placeholder="Choose a range" />
                      </SelectTrigger>
                      <SelectContent>
                        {budgets.map((b) => (
                          <SelectItem key={b} value={b}>
                            {b}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>

                <Field label="Tell us about your project">
                  <textarea
                    data-testid="contact-message"
                    rows={4}
                    className={inputCls}
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    placeholder="What do you need? Any deadlines?"
                  />
                </Field>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit"
                  className="inline-flex w-full items-center justify-center rounded-full bg-[#0B281B] px-8 py-4 font-medium text-white transition-colors hover:bg-[#1A3F2C] disabled:opacity-60"
                >
                  {submitting ? "Sending…" : "Send my request"}
                </button>
                <p className="text-center text-xs text-[#4A6657]">
                  No spam, ever. We only use your details to reply to you.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

const inputCls =
  "w-full rounded-xl border border-[#DCECE3] bg-white px-4 py-3 text-base text-[#0B281B] placeholder-[#4A6657]/50 focus:outline-none focus:ring-2 focus:ring-[#98D8BA] focus:border-transparent transition-all";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-medium text-[#0B281B]">{label}</span>
      {children}
    </label>
  );
}
